import isEmpty from 'lodash/isEmpty';

import { ASK_AICA_CONSTANTS, DEFAULT_SUGGESTED_QUESTIONS } from './constants';
import { LibraryFile, Message, ReasoningStep, SuggestedQuestion } from './actions';
import { ConversationListItem } from './types';
import { accumulateReasoning } from '../../../utils/reasoningUtils';

interface AskAicaState {
    messages: Message[];
    reasoning?: ReasoningStep;
    isLoading: boolean; //this is used for api response when a question is asked.
    isLoadingChatHistory: boolean;
    isClearingChat: boolean;
    error: string | null;
    suggestedQuestions: SuggestedQuestion[];
    activeOrgId: string | null;
    pagination: {
        pageSize: number;
        page: number;
        hasMoreData: boolean;
    };
    uiState: {
        isOpen: boolean;
        selectedSources: string[];
        rightPanelMode: 'suggestions' | 'reasoning';
    };
    library: {
        files: LibraryFile[];
        isLoading: boolean;
    };
    conversations: ConversationListItem[];
    isLoadingConversations: boolean;
    selectedConversationId: string | null;
    /** Mirrors the chat-history response's `is_deleted` for selectedConversationId
     * (Athena share-view contract) — a citation-click navigation target is often
     * a conversation absent from `conversations` entirely, so this can't be
     * derived from list membership the way setConversations' own reset does. */
    selectedConversationIsDeleted: boolean;
    /** True from New Chat click until the next turn's first conversation_id
     * chunk lands (D1/D2) — see actions.ts::setPendingNewConversation. */
    pendingNewConversation: boolean;
}

const initialState: AskAicaState = {
    messages: [],
    isLoading: false,
    isLoadingChatHistory: true,
    isClearingChat: false,
    error: null,
    suggestedQuestions: DEFAULT_SUGGESTED_QUESTIONS,
    activeOrgId: null,
    pagination: {
        pageSize: 5,
        page: 1,
        hasMoreData: true,
    },
    uiState: {
        isOpen: false,
        selectedSources: ['searchTheWeb', 'trustedPublicRecords', 'consentData', 'dataRoom'],
        rightPanelMode: 'suggestions',
    },
    library: {
        files: [],
        isLoading: false,
    },
    conversations: [],
    isLoadingConversations: false,
    selectedConversationId: null,
    selectedConversationIsDeleted: false,
    pendingNewConversation: false,
};

const addMessage = (state: AskAicaState, action: any) => {
    return {
        ...state,
        // messages: [...state.messages, action.payload],
        messages: [action.payload, ...state.messages],
    };
};

const initMessageThread = (state: AskAicaState, action: any) => {
    const { messages, conversationId } = action.payload;
    // Discard a stale response outright when it was fetched for a conversation
    // that's no longer selected (two fetchChatHistory calls for different
    // conversations can resolve out of order) — CodeRabbit PR #3721: the
    // resetChatView()-before-fetch invariant below only protects a switch's
    // OWN fetch landing on a freshly-cleared array; it does not stop an
    // OLDER, still in-flight request (e.g. a load-more) for a conversation
    // the user has since navigated away from from resolving afterward and
    // appending its stale messages onto the now-current conversation's view.
    if (conversationId !== undefined && conversationId !== state.selectedConversationId) {
        return state;
    }
    return {
        ...state,
        // Append, not replace — serves infinite-scroll pagination: MessagesContainer
        // calls fetchChatHistory(true) to load page N and dispatches
        // initialiseMessageThread with the new page's messages, which must be
        // prepended to the already-visible messages.
        // Conversation switches never produce stale interleaving because
        // ConversationSidebar dispatches resetChatView() BEFORE the fetch,
        // so state.messages is already [] when the first page arrives.
        messages: [...state.messages, ...messages],
        isLoadingChatHistory: false,
        pagination: {
            ...state.pagination,
            page: state.pagination.page + 1,
            hasMoreData: !!messages.length,
        },
    };
};

const updateMessage = (state: AskAicaState, action: any) => {
    const { id, content, metadata, status } = action.payload;
    return {
        ...state,
        messages: state.messages.map((message) =>
            message.id === id
                ? {
                      ...message,
                      // Replace with server-accumulated content when available;
                      // keep existing content when content is empty (e.g. metadata-only updates on completion).
                      content: content || message.content,
                      // Shallow merge: metadata.sources from the final chunk REPLACES any prior value
                      // (Athena emits sources only on the is_final chunk, so prior chunks have no sources).
                      metadata: { ...message.metadata, ...metadata },
                      // Promote feedback_ack from metadata to the top-level flag so both
                      // live (metadata.feedback_ack) and reload (isFeedbackAck set by history
                      // map) paths use the same field in MessagesContainer (A-14 / A-17).
                      isFeedbackAck: message.isFeedbackAck || !!metadata?.feedback_ack,
                      // Both the re-attach path and the live final chunk send a
                      // status; without it a turn's bubble would stay in its
                      // working state forever after the run ends (RECUR-28136 D12).
                      status: status ?? message.status,
                  }
                : message,
        ),
    };
};

const clearMessages = (state: AskAicaState, action: any) => {
    return {
        ...state,
        messages: [],
        pagination: {
            ...initialState.pagination,
        },
        suggestedQuestions: DEFAULT_SUGGESTED_QUESTIONS,
        activeOrgId: action.payload ?? null,
        library: {
            ...initialState.library,
        },
        reasoning: undefined,
        uiState: {
            ...state.uiState,
            rightPanelMode: 'suggestions' as const,
        },
        conversations: [],
        isLoadingConversations: false,
        selectedConversationId: null,
        selectedConversationIsDeleted: false,
        pendingNewConversation: false,
    };
};

// Resets only the chat-view slice (messages, reasoning, suggestedQuestions,
// pagination, isLoadingChatHistory). Does NOT touch conversations,
// selectedConversationId, library, or uiState — sidebar stays populated.
const resetChatView = (state: AskAicaState) => {
    return {
        ...state,
        messages: [],
        reasoning: undefined,
        suggestedQuestions: DEFAULT_SUGGESTED_QUESTIONS,
        // hasMoreData: false, NOT initialState's true — an empty, just-reset view
        // has nothing to paginate yet. Left true here, MessagesContainer's
        // InfiniteScroll auto-fires `next()` against the now-empty/under-filled
        // container (dataLength 0 doesn't fill the viewport, a known
        // react-infinite-scroll-component quirk) and calls fetchChatHistory(true)
        // before a real conversation is selected. With selectedConversationId
        // null (New Chat) that resolves to an UNCORRELATED bare history fetch —
        // effectiveConversationId ends up undefined, so the reducer's
        // overlapping-fetch guard (keyed on a defined conversationId) can't
        // reject it — which the backend serves via its own "active conversation"
        // fallback, silently repopulating the just-cleared view with the OLD
        // conversation. This was why New Chat needed several clicks to stick.
        // Whichever real fetch follows this reset (a row click, or New Chat's
        // next send) sets the real value from its own response.
        pagination: { ...initialState.pagination, hasMoreData: false },
        isLoadingChatHistory: false,
        // Cleared eagerly (not just on the next fetch's response) so a stale
        // "Deleted" badge from the PREVIOUS conversation can't flash while the
        // new one's history is in flight.
        selectedConversationIsDeleted: false,
    };
};

const setIsLoading = (state: AskAicaState, action: any) => {
    return {
        ...state,
        isLoading: action.payload,
    };
};

const setIsLoadingChatHistory = (state: AskAicaState, action: any) => {
    return {
        ...state,
        isLoadingChatHistory: action.payload,
    };
};

const setIsClearingChat = (state: AskAicaState, action: any) => {
    return {
        ...state,
        isClearingChat: action.payload,
    };
};

const setError = (state: AskAicaState, action: any) => {
    return {
        ...state,
        error: action.payload,
    };
};

const setReasoning = (state: AskAicaState, action: any) => {
    const { reasoning, hasNewReasoning } = action.payload;
    if (isEmpty(reasoning) || !hasNewReasoning) return state;
    return {
        ...state,
        reasoning: accumulateReasoning(reasoning),
    };
};

const resetReasoning = (state: AskAicaState) => {
    return {
        ...state,
        reasoning: undefined,
    };
};

// UI-related reducer functions
const setAskAicaOpen = (state: AskAicaState, action: any) => {
    return {
        ...state,
        uiState: {
            ...state.uiState,
            isOpen: action.payload,
        },
    };
};

const setAskAicaRightPanelMode = (state: AskAicaState, action: any) => {
    return {
        ...state,
        uiState: {
            ...state.uiState,
            rightPanelMode: action.payload,
        },
    };
};

const setGlobalSuggestedQuestions = (state: AskAicaState, action: any) => {
    const { questions, conversationId } = action.payload;
    // Same overlapping-fetch discard as initMessageThread above — see its
    // comment. Skipped when conversationId is undefined (a caller with
    // nothing to correlate against, e.g. a live turn's own suggestions).
    if (conversationId !== undefined && conversationId !== state.selectedConversationId) {
        return state;
    }
    return {
        ...state,
        suggestedQuestions: questions,
    };
};

const updateGlobalSuggestedQuestions = (state: AskAicaState, action: any) => {
    return {
        ...state,
        suggestedQuestions: [...state.suggestedQuestions, ...action.payload],
    };
};

const setLibraryLoading = (state: AskAicaState, action: any) => {
    return {
        ...state,
        library: {
            ...state.library,
            isLoading: action.payload,
        },
    };
};

const setLibraryFiles = (state: AskAicaState, action: any) => {
    return {
        ...state,
        library: {
            ...state.library,
            files: action.payload,
            isLoading: false,
        },
    };
};

const addLibraryFiles = (state: AskAicaState, action: any) => {
    // Null-safe: a malformed/empty dispatch must not crash the .filter below.
    const newFiles: LibraryFile[] = action.payload ?? [];
    const existingKeys = new Set(state.library.files.map((file) => file.mnemosyneDocId || file.fileUrl));
    const dedupedNewFiles = newFiles.filter((file) => !existingKeys.has(file.mnemosyneDocId || file.fileUrl));
    if (dedupedNewFiles.length === 0) return state;

    return {
        ...state,
        library: {
            ...state.library,
            files: [...dedupedNewFiles, ...state.library.files],
        },
    };
};

const setConversations = (state: AskAicaState, action: any) => {
    const conversations: ConversationListItem[] = action.payload;
    // If the currently selected conversation vanished from a REFRESH of the list
    // (e.g. it was deleted while the user was actively viewing it), reset
    // selection so the center and sidebar agree. But a conversation reached via
    // a cross-chat citation click is legitimately absent from this org-wide list
    // (GET /chat/conversations never returns soft-deleted rows) while still being
    // the user's deliberate navigation target — selectedConversationIsDeleted
    // being true here means we already knew about the deletion when we selected
    // it, so a periodic poll finding it absent must not silently kick the view
    // back to empty (this bug was found live: the 25s conversation-list poll
    // reset a citation-navigated deleted conversation out from under the user
    // within its own test window).
    const stillPresent =
        state.selectedConversationId === null ||
        state.selectedConversationIsDeleted ||
        conversations.some((c) => c.conversation_id === state.selectedConversationId);
    return {
        ...state,
        conversations,
        isLoadingConversations: false,
        selectedConversationId: stillPresent ? state.selectedConversationId : null,
        // F-2: arming New Chat here (not just clearing the selection) is what
        // makes the NEXT send force-create — without it, sendMessage goes out
        // with neither an id nor the flag, and the server's active-conversation
        // fallback silently resumes the second-most-recent conversation instead.
        pendingNewConversation: stillPresent ? state.pendingNewConversation : true,
    };
};

const setLoadingConversations = (state: AskAicaState, action: any) => {
    return {
        ...state,
        isLoadingConversations: action.payload,
    };
};

const setSelectedConversation = (state: AskAicaState, action: any) => {
    return {
        ...state,
        selectedConversationId: action.payload,
        // A conversation becoming explicitly selected — whether via row click,
        // back-to-own, or the stream echoing an id for a New Chat turn —
        // supersedes any pending New Chat intent (D2).
        pendingNewConversation: false,
    };
};

const setPendingNewConversation = (state: AskAicaState, action: any) => {
    return {
        ...state,
        pendingNewConversation: action.payload,
    };
};

const setSelectedConversationIsDeleted = (state: AskAicaState, action: any) => {
    // Only apply when the response this was dispatched for still matches what's
    // currently selected — a late response for a conversation the user has since
    // navigated away from must not clobber the current conversation's deletion
    // state (see the doc comment on the setSelectedConversationIsDeleted action).
    if (action.payload.conversationId !== state.selectedConversationId) return state;
    return {
        ...state,
        selectedConversationIsDeleted: action.payload.isDeleted,
    };
};

const askAicaReducer = (state: AskAicaState = initialState, action: any): AskAicaState => {
    switch (action.type) {
        // Chat functionality actions
        case ASK_AICA_CONSTANTS.INITIALISE_MESSAGE_THREAD:
            return initMessageThread(state, action);
        case ASK_AICA_CONSTANTS.ADD_MESSAGE:
            return addMessage(state, action);
        case ASK_AICA_CONSTANTS.UPDATE_MESSAGE:
            return updateMessage(state, action);
        case ASK_AICA_CONSTANTS.CLEAR_MESSAGES:
            return clearMessages(state, action);
        case ASK_AICA_CONSTANTS.SET_IS_LOADING:
            return setIsLoading(state, action);
        case ASK_AICA_CONSTANTS.SET_IS_LOADING_CHAT_HISTORY:
            return setIsLoadingChatHistory(state, action);
        case ASK_AICA_CONSTANTS.SET_IS_CLEARING_CHAT:
            return setIsClearingChat(state, action);
        case ASK_AICA_CONSTANTS.SET_ERROR:
            return setError(state, action);
        case ASK_AICA_CONSTANTS.SET_REASONING:
            return setReasoning(state, action);
        case ASK_AICA_CONSTANTS.RESET_REASONING:
            return resetReasoning(state);

        // UI-related actions
        case ASK_AICA_CONSTANTS.SET_ASK_AICA_OPEN:
            return setAskAicaOpen(state, action);
        case ASK_AICA_CONSTANTS.SET_ASK_AICA_RIGHT_PANEL_MODE:
            return setAskAicaRightPanelMode(state, action);
        case ASK_AICA_CONSTANTS.SET_GLOBAL_SUGGESTED_QUESTIONS:
            return setGlobalSuggestedQuestions(state, action);
        case ASK_AICA_CONSTANTS.UPDATE_GLOBAL_SUGGESTED_QUESTIONS:
            return updateGlobalSuggestedQuestions(state, action);

        // Library-related actions
        case ASK_AICA_CONSTANTS.SET_LIBRARY_LOADING:
            return setLibraryLoading(state, action);
        case ASK_AICA_CONSTANTS.SET_LIBRARY_FILES:
            return setLibraryFiles(state, action);
        case ASK_AICA_CONSTANTS.ADD_LIBRARY_FILES:
            return addLibraryFiles(state, action);

        // Conversation list actions
        case ASK_AICA_CONSTANTS.SET_CONVERSATIONS:
            return setConversations(state, action);
        case ASK_AICA_CONSTANTS.SET_LOADING_CONVERSATIONS:
            return setLoadingConversations(state, action);
        case ASK_AICA_CONSTANTS.SET_SELECTED_CONVERSATION:
            return setSelectedConversation(state, action);
        case ASK_AICA_CONSTANTS.RESET_CHAT_VIEW:
            return resetChatView(state);
        case ASK_AICA_CONSTANTS.SET_PENDING_NEW_CONVERSATION:
            return setPendingNewConversation(state, action);
        case ASK_AICA_CONSTANTS.SET_SELECTED_CONVERSATION_IS_DELETED:
            return setSelectedConversationIsDeleted(state, action);
        default:
            return state;
    }
};

export default askAicaReducer;
